
import { Component, Vue } from 'vue-property-decorator'
import keyCodeMapperCharacter from '@/utils/keycodemap.utils'
import * as onScan from 'onscan.js'
import ProductProvider from '@/resources/product.provider'
import StockMappingProvider from '@/resources/stock-mapping.provider'

@Component({
  components: {}
})
export default class StockMapping extends Vue {
  productService = new ProductProvider()

  stockMappingService = new StockMappingProvider()

  location: any = null

  products: any = []

  loadingLocation = false

  loadingProduct = false

  loadingMapping = false

  currentQty = 0

  storeChoiceModal = false

  storeChoices: any[] = []

  mounted (): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this
    onScan.attachTo(document, {
      suffixKeyCodes: [13],
      reactToPaste: true,
      onScan (sCode) {
        if (_this.storeChoiceModal) {
          _this.$buefy.toast.open({ message: 'กรุณาเลือก Store ก่อน', type: 'is-warning' })
          return
        }
        const patternUrl = /^(https:\/\/www\.gentlewomanonline\.com\/|https:\/\/gentlewomanonline\.com\/|https:\/\/www\.matter-makers\.com\/|https:\/\/matter-makers\.com\/)/
        const patternOldLocation = /^location::/
        const scanned = sCode.trim()
        if (patternUrl.test(scanned.toLowerCase())) {
          const splitCodeFromUrl = scanned.split('/')
          const code = splitCodeFromUrl[splitCodeFromUrl.length - 1]
          if (code) _this.addToMappingList(code)
        } else if (patternOldLocation.test(scanned)) {
          _this.$buefy.toast.open({ message: 'QR Location แบบเก่าใช้กับ Stock Mapping ไม่ได้', type: 'is-danger' })
        } else if (scanned) {
          // QR ของ location คือชื่อ location
          _this.getLocation(scanned)
        } else {
          _this.$buefy.toast.open({ message: 'QR Code นี้ไม่สามารถใช้งานได้', type: 'is-danger' })
        }
      },
      keyCodeMapper (oEvent: any) {
        // onScan ตัด space ทิ้ง แต่ชื่อ location มีช่องว่างได้
        if (+oEvent.keyCode === 32) return ' '
        if (keyCodeMapperCharacter[+oEvent.keyCode] !== undefined) {
          return keyCodeMapperCharacter[+oEvent.keyCode][+oEvent.shiftKey]
        }
        return onScan.decodeKeyEvent(oEvent)
      }
    })
  }

  get sumQty (): number {
    return this.products.reduce((sum: number, product: any) => sum + (Number(product.qty) || 0), 0)
  }

  async getLocation (name: string): Promise<void> {
    try {
      this.loadingLocation = true
      const { data } = await this.stockMappingService.getLocationByName(name)
      if (data) {
        this.location = data
        this.getCurrentQty(data.id)
      } else {
        this.$buefy.toast.open({ message: `ไม่พบ location: ${name}`, type: 'is-danger' })
      }
    } catch (error: any) {
      const message = error?.code === 404
        ? `ไม่พบ location: ${name}`
        : 'เกิดข้อผิดพลาดในการดึงข้อมูล location'
      this.$buefy.toast.open({ message, type: 'is-danger' })
    } finally {
      this.loadingLocation = false
    }
  }

  async getCurrentQty (locationId: string): Promise<void> {
    try {
      const { data } = await this.stockMappingService.getSumQtyByLocation(locationId)
      // สแกน location อื่นระหว่างรอ: ไม่เอาผลของ location เก่ามาทับ
      if (this.location?.id === locationId) this.currentQty = data?.totalQty ?? 0
    } catch (error) {
      this.currentQty = 0
      this.$buefy.toast.open({ message: 'เกิดข้อผิดพลาดในการดึงจำนวนสินค้าใน location', type: 'is-danger' })
    }
  }

  async addToMappingList (code: string): Promise<void> {
    // สแกนซ้ำ: บวกเข้าแถวล่าสุดของ code นี้ ไม่ต้องเลือก store ใหม่
    const isExistingIndex = this.products.findIndex((p: any) => p.code === code)
    if (isExistingIndex === -1) {
      this.getProductInfo(code)
    } else {
      this.bumpItem(isExistingIndex)
    }
  }

  bumpItem (idx: number): void {
    // ลบช่อง qty จนว่าง v-model.number จะได้ '' แล้ว += 1 กลายเป็นต่อ string ('1', '11', ...)
    this.products[idx].qty = (Number(this.products[idx].qty) || 0) + 1
    this.products[idx].timeStamp = Date.now()
    this.products.sort((a: any, b: any) => b.timeStamp - a.timeStamp)
  }

  pushProduct (sku: any): void {
    const rowKey = `${sku.code}::${sku.store?.id}`
    const isExistingIndex = this.products.findIndex((p: any) => p.rowKey === rowKey)
    if (isExistingIndex === -1) {
      this.products.push({ ...sku, rowKey, qty: 1, timeStamp: Date.now() })
      this.products.sort((a: any, b: any) => b.timeStamp - a.timeStamp)
    } else {
      this.bumpItem(isExistingIndex)
    }
  }

  selectStore (sku: any): void {
    this.storeChoiceModal = false
    this.storeChoices = []
    this.pushProduct(sku)
  }

  saveMapping (): void {
    this.$buefy.dialog.alert({
      title: 'บันทึก Stock Mapping',
      message: 'ต้องการบันทึก Stock Mapping หรือไม่',
      confirmText: 'บันทึก',
      cancelText: 'ยกเลิก',
      canCancel: true,
      onConfirm: () => {
        this.mappingLocation()
      },
      type: 'is-info',
      hasIcon: true
    })
  }

  async mappingLocation (): Promise<void> {
    // call api save
    try {
      this.loadingMapping = true
      await this.stockMappingService.mappingLocation(
        {
          location: this.location.id,
          productSkus: this.products.map((p: any) => ({ qty: p.qty, code: p.code, storeId: p.store?.id }))
        }
      )
      this.$buefy.toast.open({ message: 'การบันทึกสำเร็จ', type: 'is-success' })
      this.location = null
      this.currentQty = 0
      this.products = []
    } catch (error) {
      this.$buefy.toast.open({ message: 'การบันทึกผิดพลาด', type: 'is-danger' })
    } finally {
      this.loadingMapping = false
    }
  }

  // แยกท้ายชื่อ (หลัง " - " ตัวสุดท้าย) ไว้แสดงเต็ม ส่วนหน้าตัดด้วย ... ให้พอ 1 บรรทัด
  // eslint-disable-next-line class-methods-use-this
  splitName (name = ''): { head: string, tail: string } {
    const parts = name.split(' - ')
    if (parts.length < 2) return { head: name, tail: '' }
    return { head: parts.slice(0, -1).join(' - ').trim(), tail: parts[parts.length - 1].trim() }
  }

  // eslint-disable-next-line class-methods-use-this
  defaultEmptyObject (obj: any, key: string, defaultValue: string): string {
    return obj && obj[key] ? obj[key] : defaultValue
  }

  incrementItem (rowKey: string): void {
    const idx = this.products.findIndex((p: any) => p.rowKey === rowKey)
    if (idx !== -1) this.products[idx].qty = (Number(this.products[idx].qty) || 0) + 1
  }

  decrementItem (rowKey: string): void {
    const idx = this.products.findIndex((p: any) => p.rowKey === rowKey)
    if (idx !== -1) {
      const qty = Number(this.products[idx].qty) || 0
      if (qty <= 1) this.products.splice(idx, 1)
      else this.products[idx].qty = qty - 1
    }
  }

  removeItem (rowKey: string): void {
    const idx = this.products.findIndex((p: any) => p.rowKey === rowKey)
    if (idx !== -1) this.products.splice(idx, 1)
  }

  async getProductInfo (code: string): Promise<void> {
    // get from api
    try {
      this.loadingProduct = true
      // ค้นทุก store: code เดียวกันอาจได้กลับมาหลาย store
      const { data: productSku } = await this.productService.getProductByCodeAllStore({
        skuCodes: [code]
      })
      const skus = productSku || []
      if (skus.length === 1) {
        this.pushProduct(skus[0])
      } else if (skus.length > 1) {
        this.storeChoices = skus
        this.storeChoiceModal = true
      } else {
        this.$buefy.toast.open({ message: 'QR Code นี้ไม่สามารถใช้งานได้', type: 'is-danger' })
      }
    } catch (error) {
      this.$buefy.toast.open({ message: 'เกิดข้อผิดพลาดในการดึงข้อมูลสินค้า', type: 'is-danger' })
    } finally {
      this.loadingProduct = false
    }
  }

  // eslint-disable-next-line class-methods-use-this
  beforeDestroy (): void {
    onScan.detachFrom(document)
  }
}
